import { matchesNestedKeyword } from '../../shared/utils/keyword-filter';
import { JstDate } from '../../core/time/business-time';
import { Component, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DatePipe, DecimalPipe } from '@angular/common';
import { PageTitleService } from '../../core/services/page-title.service';
import { StudioService } from '../../core/services/studio.service';
import { TransactionService } from '../../core/services/transaction.service';
import { InvoiceService } from '../../core/services/invoice.service';
import { MemberService } from '../../core/services/member.service';
import { StudioSelectionService } from '../../core/services/studio-selection.service';
import { PagerComponent } from '../../shared/components/pager.component';

const SESSION_KEY = 'invoice_select_studio_id';

function buildYearOptions() {
  const now = new JstDate();
  const opts = [];
  for (let y = 2015; y <= now.getFullYear() + 1; y++) {
    opts.push({ id: y, name: `${y}年` });
  }
  return opts;
}

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({ id: i + 1, name: `${i + 1}月` }));

const PAYMENT_METHOD_OPTIONS = [
  { id: '0', name: '口座振替' }, { id: '1', name: 'クレジットカード' },
];

const STATUS_OPTIONS = [
  { id: 0, name: '請求予定' }, { id: 2, name: '請求確定' }, { id: 3, name: '承認済' },
  { id: 5, name: '入金済' }, { id: 6, name: '入金エラー' },
];

function normalizeTransactionIds(value: unknown): number[] {
  const values = Array.isArray(value) ? value : String(value ?? '').split(',');
  return values
    .map(id => Number(String(id).trim()))
    .filter(id => Number.isFinite(id) && id > 0);
}

declare const Encoding: any;

type BtExportRow = { aa: string };

function normalizeBankCodeForZengin(bankCode: any): any {
  return String(bankCode) === '9901' ? '9900' : bankCode;
}

function createOutFileName(btMethod: number, trusteeCode: string, filePrefix: string): string {
  let fileName = 'R';
  let code = '_all_data';
  if (btMethod === 1) {
    code = '32368';
    fileName = 'S';
  }
  return `${fileName}${code}_${filePrefix}.txt`;
}

function getExportObj(data: any[] = [], issueMonth: string): { fileName: string | null; data: BtExportRow[] | null } {
  const exportData: { fileName: string | null; data: BtExportRow[] | null } = { fileName: null, data: null };
  const rows: BtExportRow[] = [];
  let aplusDataCount = 0;
  let aplusSumAmount = 0;

  if (data.length > 0) {
    let beforeStudioId = 0;
    let dataCount = 0;
    let totalAmount = 0;
    data.forEach((v, i) => {
      const studioId = v.studioId;
      const trusteeCode = v.trusteeCode;
      const btMethod = Number(v.btMethod);
      const sid = v.sid;
      const amount = Number(v.amount);
      let accountName = String(v.btAccountName ?? '');
      const accountType = v.btAccountType;
      const cardNumber = v.btCardNum;
      const bankCode = btMethod === 2 ? normalizeBankCodeForZengin(v.btBankCode) : v.btBankCode;
      const branchCode = v.btBranchCode;
      const bankName = v.btBankName ?? '';
      const branchName = v.btBranchName ?? '';
      const withdrawalDate = `${issueMonth.split('-')[1]}28`;

      let row: BtExportRow;
      if (btMethod === 1) {
        if (!i) {
          rows.push({ aa: `19113236890200ｶ)ｽﾏｰﾄﾌｨｯﾄﾈｽ${' '.repeat(28)}${issueMonth.slice(-2)}27${' '.repeat(62)}` });
        }
        let requiredSpaceAfterName = 30 - accountName.length;
        if (requiredSpaceAfterName < 0) {
          requiredSpaceAfterName = 0;
          accountName = accountName.substring(0, 30);
        }
        row = {
          aa: '2'
            + String(bankCode)
            + ' '.repeat(15)
            + String(branchCode)
            + ' '.repeat(19)
            + String(accountType)
            + (`0000000${String(cardNumber)}`).slice(-7)
            + accountName
            + ' '.repeat(requiredSpaceAfterName)
            + (`0000000000${String(amount)}`).slice(-10)
            + '10'
            + String(sid)
            + ' '.repeat(8),
        };
        aplusDataCount++;
        aplusSumAmount += amount;
      } else {
        if (i && beforeStudioId !== studioId) {
          rows.push({
            aa: '8'
              + (`000000${String(dataCount)}`).slice(-6)
              + (`000000000000${String(totalAmount)}`).slice(-12)
              + '000000'
              + '000000000000'
              + '000000'
              + '000000000000'
              + ' '.repeat(65),
          });
          dataCount = 0;
          totalAmount = 0;
        }
        if (beforeStudioId !== studioId) {
          rows.push({
            aa: '1'
              + '91'
              + '0'
              + '00000'
              + String(trusteeCode)
              + `ｶ)ｽﾏ-ﾄﾌｲﾂﾄﾈｽ${' '.repeat(28)}`
              + withdrawalDate
              + '0010'
              + `ﾘｿﾅ${' '.repeat(12)}`
              + '539'
              + `ｳﾗﾔｽ${' '.repeat(11)}`
              + '1'
              + '1346121'
              + ' '.repeat(17),
          });
        }
        let requiredSpaceAfterName = 30 - accountName.length;
        if (requiredSpaceAfterName < 0) {
          requiredSpaceAfterName = 1;
        }
        row = {
          aa: '2'
            + String(bankCode)
            + (`${bankName}${' '.repeat(15)}`).slice(0, 15)
            + String(branchCode)
            + (`${branchName}${' '.repeat(15)}`).slice(0, 15)
            + ' '.repeat(4)
            + String(accountType)
            + (`0000000${String(cardNumber)}`).slice(-7)
            + (`${accountName}${' '.repeat(requiredSpaceAfterName)}`).slice(0, 30)
            + (`0000000000${String(amount)}`).slice(-10)
            + '0'
            + String(sid)
            + '0'
            + ' '.repeat(8),
        };
        dataCount++;
        totalAmount += amount;
        beforeStudioId = studioId;
      }

      if (!i) {
        exportData.fileName = createOutFileName(btMethod, trusteeCode, issueMonth);
      }
      rows.push(row);

      if (btMethod === 1 && i + 1 === data.length) {
        rows.push({ aa: `8${(`000000${String(aplusDataCount)}`).slice(-6)}${(`000000000000${String(aplusSumAmount)}`).slice(-12)}${'0'.repeat(36)}${' '.repeat(65)}` });
        rows.push({ aa: `9${' '.repeat(119)}` });
      } else if (btMethod === 2 && i + 1 === data.length) {
        rows.push({
          aa: '8'
            + (`000000${String(dataCount)}`).slice(-6)
            + (`000000000000${String(totalAmount)}`).slice(-12)
            + '000000'
            + '000000000000'
            + '000000'
            + '000000000000'
            + ' '.repeat(65),
        });
        rows.push({ aa: `9${' '.repeat(119)}` });
      }
    });
    exportData.data = rows;
  }

  return exportData;
}

function toShiftJis(value: string): Uint8Array<ArrayBuffer> {
  const detected = Encoding.detect(value);
  const unicodeList: number[] = [];
  for (let i = 0; i < value.length; i += 1) {
    unicodeList.push(value.charCodeAt(i));
  }
  const sjisArray = Encoding.convert(unicodeList, { to: 'SJIS', from: detected });
  return new Uint8Array(sjisArray);
}

@Component({
    selector: 'app-invoice-list',
    imports: [RouterLink, FormsModule, DatePipe, DecimalPipe, PagerComponent],
    template: `
<div class="animated fadeIn">
  <div class="row sfapp-container">
    <div class="col-md-12">
      <div class="card">
        <div class="card-header">
          <span><i class="fa s-icon-list"></i> 一覧 @if (selectedStudio) {<span>(全 {{ filteredItems.length }} 件)</span>}</span>
        </div>
        <div class="card-body">
          <form novalidate name="myStudioForm">
            <strong>店舗選択</strong>
            <hr />
            <div class="row">
              <div class="col-md-4">
                <div class="form-group">
                  <label class="col-form-label" for="studio_name">店舗</label>
                  <select class="form-control" id="studio_name" [(ngModel)]="selectedStudio"
                    name="studio_name" (ngModelChange)="onStudioChange()">
                    <option [ngValue]="null">-- 選択してください --</option>
                    @for (studio of studios; track studio.id) {
                      <option [ngValue]="studio">{{ studio.name }}</option>
                    }
                  </select>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group row">
                  <div class="col-md-12">
                    <label class="col-form-label" for="year_pay">請求締め月</label>
                    <div class="bisected-input-wrap">
                      <div class="bisected-input">
                        <select class="form-control" id="year_pay" [(ngModel)]="selectedYear"
                          name="year_pay" (ngModelChange)="loadInvoice()">
                          @for (opt of yearOptions; track opt.id) {
                            <option [ngValue]="opt.id">{{ opt.name }}</option>
                          }
                        </select>
                      </div>
                      <div><span>&nbsp;</span></div>
                      <div class="bisected-input">
                        <select class="form-control" id="month_pay" [(ngModel)]="selectedMonth"
                          name="month_pay" (ngModelChange)="loadInvoice()">
                          @for (opt of monthOptions; track opt.id) {
                            <option [ngValue]="opt.id">{{ opt.name }}</option>
                          }
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              @if (showAlert) {
                <div class="col-md-12">
                  <div class="alert alert-success" role="alert">
                    <button type="button" class="close" (click)="showAlert = false" aria-label="Close">
                      <span aria-hidden="true">&times;</span>
                    </button>
                    <p>請求データは、毎月12日を締め日として自動で作成されます。したがって、{{ selectedYear }}年{{ selectedMonth }}月12日までは、請求データは0(ゼロ)件となります。</p>
                    <hr>
                    <p>口振結果は金融機関反映日の2日後の2:00にインポートが自動で実行されます。</p>
                  </div>
                </div>
              }
            </div>
          </form>
          @if (btData?.id) {
            <div class="row">
              @if (btData.importFilePath) {
                <div class="col-md-12">
                  <div class="alert alert-warning" role="alert">
                    <button type="button" class="close" (click)="btData.importFilePath = null" aria-label="Close">
                      <span aria-hidden="true">&times;</span>
                    </button>
                    <p>インポート用データファイルをアップロード済みです。</p>
                  </div>
                </div>
              }
              <div class="col-md-12">
                <table class="table table-bordered table-sm">
                  <thead>
                    <tr>
                      <th class="border-right-0">データダウンロード・アップロード</th>
                      <th class="border-left-0" style="width: 10%;"></th>
                      <th>請求件数</th>
                      <th>請求額</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="border-right-0">
                        口座振替データ
                        @if (btData.id) { <span>&nbsp;({{ btData.trusteeCode }})</span> }
                      </td>
                      <td class="text-center border-left-0">
                        <button class="mt-1 mb-1 btn btn-success btn-sm" type="button" (click)="showUploadModal = true" title="アップロード"><i class="fa fa-upload"></i></button>
                        <button class="mt-1 mb-1 btn btn-success btn-sm" type="button" (click)="downloadBT()" [disabled]="downloading || !btCsvData || !btCsvFileName" title="ダウンロード"><i class="fa s-icon-dawnload"></i></button>
                      </td>
                      <td><span class="pull-right">{{ btData.count | number }}</span></td>
                      <td><span class="pull-right">¥{{ btData.amount | number:'1.0-0' }}</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          }
        </div>
        @if (selectedStudio) {
          <div>
            @if (showFilterOption) {
              <div class="card-body">
                <strong>請求データ詳細</strong>
                <hr />
                <form novalidate name="myListForm">
                  <div class="row" style="align-items: flex-end;">
                    <div class="col-md-4">
                      <div class="form-group">
                        <label class="col-form-label" for="pay_method">支払方法</label>
                        <select class="form-control" id="pay_method" [(ngModel)]="paymentMethodFilter" (ngModelChange)="pageIndex = 0" name="pay_method">
                          <option [ngValue]="null">-- すべて --</option>
                          @for (opt of paymentMethodOptions; track opt.id) {
                            <option [ngValue]="opt">{{ opt.name }}</option>
                          }
                        </select>
                      </div>
                    </div>
                    <div class="col-md-8">
                      <div class="form-group">
                        <label class="col-form-label" for="keyword">キーワード検索</label>
                        <input id="keyword" name="keyword" type="text" [(ngModel)]="keyword"
                          class="form-control" placeholder="会員ID、会員氏名など"
                          (ngModelChange)="pageIndex = 0">
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group row">
                        <div class="col-md-12">
                          <button class="btn btn-warning text-white" type="button" (click)="clearFilter()">
                            <i class="fa s-icon-backspace"></i>&nbsp;条件クリア
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <hr>
                </form>
              </div>
            }
          <div class="card-body">
          <app-pager [pageIndex]="pageIndex" [totalPages]="totalPages" (pageChange)="goToPage($event)"></app-pager>
          @if (searching) {
            <div class="text-center py-3"><span class="fa fa-spinner fa-spin fa-2x"></span></div>
          }
          @if (selectedStudio && !searching && filteredItems.length === 0) {
            <span>該当するデータはありません</span>
          }
          @if (filteredItems.length > 0) {
            <table class="table table-bordered table-sm table-hover table-ptb-0">
              <thead>
                <tr>
                  <th style="width: 3%;"></th>
                  <th style="width: 10%;">状態</th>
                  <th>会員ID</th>
                  <th>会員氏名</th>
                  <th>金額</th>
                  <th>支払方法</th>
                  <th style="width: 10%;"></th>
                </tr>
              </thead>
              <tbody>
                @for (item of pagedItems; track item.id) {
                  <tr>
                    <td class="text-center text-white"
                      [class.bg-warning]="item.status === 6 && item.retryCount === 0"
                      [class.bg-danger]="item.status === 6 && item.retryCount > 0 && item.retryCount !== 9 && item.retryCount !== 99">
                      @if (item.status === 6 && item.retryCount !== 9 && item.retryCount !== 99) { {{ item.retryCount + 1 }} }
                    </td>
                    <td>
                      <div class="d-flex flex-column bd-highlight text-center">
                        @if (item.status === 0) { <span class="text-warning" title="請求予定"><i class="fa s-icon-schedule fa-2x fa-fw"></i></span> }
                        @if (item.status === 1) { <span class="text-secondary" title="削除済"><i class="fa s-icon-close fa-2x fa-fw"></i></span> }
                        @if (item.status === 2) { <span class="text-warning" title="請求確定"><i class="fa s-icon-waiting fa-2x fa-fw"></i></span> }
                        @if (item.status === 3) { <span class="text-primary" title="承認済"><i class="fa fa-gavel fa-2x fa-fw"></i></span> }
                        @if (item.status === 4) { <span class="text-secondary" title="キャンセル済"><i class="fa fa-times-circle fa-2x fa-fw"></i></span> }
                        @if (item.status === 5) {
                          <span [class.text-success]="!item.isOvercharged" [class.text-danger]="item.isOvercharged" title="入金済">
                            <i class="fa s-icon-finished fa-2x fa-fw"></i>
                          </span>
                        }
                        @if (item.status === 6) { <span class="text-danger" title="入金エラー"><i class="fa s-icon-attention fa-2x fa-fw"></i></span> }
                        @if (item.status === 7) { <span class="text-success" title="払い戻し済"><i class="fa s-icon-back fa-2x fa-fw"></i></span> }
                        @if (item.status === 8) { <span class="text-secondary" title="請求停止"><span class="material-icons-outlined fa-2x fa-fw">do_disturb_on</span></span> }
                        @if (item.status === 9) { <span class="text-success" title="受け取り"><i class="fa s-icon-cashPay fa-2x fa-fw"></i></span> }
                        <div>
                          @if (item.nowStatus === 0 || item.nowStatus === 6) {
                            <button type="button" class="btn btn-primary text-white btn-sm ml-1 mr-1 mb-1"
                              style="padding-top: 0.1rem !important;padding-bottom: 0.1rem !important;"
                              (click)="setPayInCash(item)" title="受け取り処理">
                              <i class="fa s-icon-settlement fa-fw"></i>
                            </button>
                          }
                        </div>
                      </div>
                    </td>
                    <td>{{ item.member?.memberCode }}</td>
                    <td>
                      <a href="" (click)="$event.preventDefault(); setMemberDetail(item.member)">
                        {{ item.member?.lastName }}&nbsp;{{ item.member?.firstName }}
                      </a>
                    </td>
                    <td><span class="pull-right">¥{{ item.amount | number:'1.0-0' }}</span></td>
                    <td>
                      @if (item.paymentMethod === 0) { <span>口座振替</span> }
                      @if (item.paymentMethod === 1) { <span>クレジットカード</span> }
                    </td>
                    <td class="text-center">
                      <a class="mt-1 mb-1 btn btn-warning btn-sm text-white"
                        [routerLink]="['/transaction', item.member?.id, 'all']" title="課金一覧">
                        <i class="fa s-icon-salesList fa-fw"></i>
                      </a>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          }
          <app-pager [pageIndex]="pageIndex" [totalPages]="totalPages" (pageChange)="goToPage($event)"></app-pager>
          </div>
        </div>
        }
      </div>
    </div>
  </div>
</div>

<!-- 受け取り処理モーダル -->
@if (showPayInCashModal) {
  <div class="modal-backdrop fade show" (click)="showPayInCashModal = false"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-labelledby="payInCashModalLabel">
    <div class="modal-dialog" role="document">
      <div class="modal-content border-danger">
        <div class="modal-header bg-danger">
          <h5 class="modal-title" id="payInCashModalLabel"><i class="fa s-icon-attention"></i> 受け取り処理</h5>
          <button type="button" class="close" (click)="showPayInCashModal = false" aria-label="Close">
            <span class="text-white" aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body" style="background-color: #FFF6F8 !important">
          <div class="form-group row">
            <label class="col-md-12 col-form-label">変更ボタンを押すと状態にかかわらず、<br>関係するすべての請求が「受け取り」となります。</label>
          </div>
          <div class="form-group">
            <table class="table table-sm table-borderless">
              <tr><th class="text-right" style="width: 20%;">会員ID：</th><td>{{ picItem?.member?.memberCode }}</td></tr>
              <tr><th class="text-right">氏名：</th><td>{{ picItem?.member?.lastName }}&nbsp;{{ picItem?.member?.firstName }}</td></tr>
              <tr><th class="text-right">金額：</th><td>¥{{ picItem?.amount | number:'1.0-0' }}</td></tr>
            </table>
          </div>
        </div>
        <div class="modal-footer" style="background-color: #FFF6F8 !important">
          <button class="btn bg-close text-white" type="button" (click)="showPayInCashModal = false"><i class="fa s-icon-cancel"></i>&nbsp;閉じる</button>
          @if (picItem?.nowStatus === 0 || picItem?.nowStatus === 6) {
            <button class="btn btn-primary text-white" type="button" (click)="payInCash()"><i class="fa s-icon-turnOver"></i>&nbsp;変更</button>
          }
        </div>
      </div>
    </div>
  </div>
}

<!-- ファイルアップロードモーダル -->
@if (showUploadModal) {
  <div class="modal-backdrop fade show" (click)="showUploadModal = false"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-labelledby="fileUploadModalLabel">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="fileUploadModalLabel">口座振替データファイルアップロード</h5>
          <button type="button" class="close" (click)="showUploadModal = false" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="input-group">
            <label class="input-group-btn">
              <span class="btn btn-light">
                <i class="fa s-icon-image"></i>&nbsp;ファイル選択
                <input type="file" file-model="myFile" class="d-none" (change)="onFileChange($event)">
              </span>
            </label>
            <input type="text" class="form-control bg-white border-0" readonly
              [value]="selectedFile?.name || '選択されていません'">
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn bg-close text-white" type="button" (click)="showUploadModal = false"><i class="fa s-icon-cancel"></i>&nbsp;閉じる</button>
          <button class="btn btn-success" type="button" (click)="uploadFile()" [disabled]="!selectedFile || uploading"><i class="fa fa-upload"></i>&nbsp;アップロード</button>
        </div>
      </div>
    </div>
  </div>
}

<!-- 会員詳細モーダル -->
@if (showMemberModal) {
  <div class="modal-backdrop fade show" (click)="showMemberModal = false"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-labelledby="getMemberDetailModalLabel">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="getMemberDetailModalLabel">会員情報 / 詳細</h5>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <table class="table table-sm table-borderless">
              <tr>
                <th class="text-right" style="width: 40%;">会員ID：</th>
                <td>{{ memberDetail?.memberCode }}</td>
              </tr>
              <tr>
                <th class="text-right">会員氏名（かな）：</th>
                <td>{{ memberDetail?.lastName }} {{ memberDetail?.firstName }}({{ memberDetail?.lastNameKana }} {{ memberDetail?.firstNameKana }})</td>
              </tr>
              <tr>
                <th class="text-right">生年月日：</th>
                <td>{{ memberDetail?.birthday }}</td>
              </tr>
              <tr>
                <th class="text-right">住所：</th>
                <td>〒{{ memberDetail?.zip }}</td>
              </tr>
              <tr>
                <th class="text-right"></th>
                <td>{{ memberDetail?.province }}&nbsp;{{ memberDetail?.city }}</td>
              </tr>
              <tr>
                <th class="text-right"></th>
                <td>{{ memberDetail?.area }}&nbsp;{{ memberDetail?.address }}</td>
              </tr>
              <tr>
                <th class="text-right">電話番号：</th>
                <td>{{ memberDetail?.phone }}</td>
              </tr>
              <tr>
                <th class="text-right">メールアドレス：</th>
                <td>{{ memberDetail?.email }}</td>
              </tr>
              <tr>
                <th class="text-right">コース区分：</th>
                <td>{{ memberDetail?.studioPlan?.name }}</td>
              </tr>
              <tr>
                <th class="text-right">支払方法：</th>
                <td>
                  @if (memberDetail?.payment?.paymentMethod === 0) { <span>口座振替</span> }
                  @if (memberDetail?.payment?.paymentMethod === 1) { <span>クレジット</span> }
                </td>
              </tr>
              <tr>
                <th class="text-right">入退館キー：</th>
                <td>{{ memberDetail?.keyNumber?.keyNumber }}</td>
              </tr>
              <tr>
                <th class="text-right">ロッカー：</th>
                <td>{{ memberDetail?.lockerNumber?.lockerNumber }}</td>
              </tr>
              <tr>
                <th class="text-right">登録日時：</th>
                <td>{{ memberDetail?.createdAt | date: 'yyyy/MM/dd HH:mm' }}</td>
              </tr>
            </table>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn bg-close text-white" type="button" (click)="showMemberModal = false">
            <i class="fa s-icon-cancel fa-fw"></i>&nbsp;閉じる
          </button>
        </div>
      </div>
    </div>
  </div>
}
  `
})
export class InvoiceListComponent implements OnInit {
  private pageTitleService = inject(PageTitleService);
  private studioService = inject(StudioService);
  private transactionService = inject(TransactionService);
  private invoiceService = inject(InvoiceService);
  private memberService = inject(MemberService);
  private studioSelection = inject(StudioSelectionService);

  studios: any[] = [];
  selectedStudio: any = null;
  items: any[] = [];
  keyword = '';
  paymentMethodFilter: any = null;
  statusFilter: any = null;
  searching = false;
  private invoiceRequestId = 0;
  showFilterOption = false;
  showAlert = true;
  selectedYear: number;
  selectedMonth: number;

  // Pagination
  readonly pageLimit = 100;
  pageIndex = 0;

  // Member detail modal
  showMemberModal = false;
  memberDetail: any = null;

  // Pay in cash modal
  showPayInCashModal = false;
  picItem: any = null;

  // BT data section
  btData: any = null;
  btCsvData: BtExportRow[] | null = null;
  btCsvFileName = '';
  downloading = false;
  showUploadModal = false;
  selectedFile: File | null = null;
  uploading = false;

  readonly yearOptions = buildYearOptions();
  readonly monthOptions = MONTH_OPTIONS;
  readonly paymentMethodOptions = PAYMENT_METHOD_OPTIONS;
  readonly statusOptions = STATUS_OPTIONS;

  constructor() {
    const now = new JstDate();
    this.selectedYear = now.getFullYear();
    this.selectedMonth = now.getMonth() + 1;
  }

  get filteredItems(): any[] {
    let list = this.items.filter(item => matchesNestedKeyword(item, this.keyword));
    if (this.paymentMethodFilter != null) {
      list = list.filter(i => String(i.paymentMethod) === String(this.paymentMethodFilter.id));
    }
    if (this.statusFilter != null) {
      list = list.filter(i => i.status === this.statusFilter.id);
    }
    return list;
  }

  get pagedItems(): any[] {
    return this.filteredItems.slice(this.pageIndex * this.pageLimit, (this.pageIndex + 1) * this.pageLimit);
  }

  get totalPages(): number { return Math.ceil(this.filteredItems.length / this.pageLimit); }

  goToPage(p: number): void {
    this.pageIndex = Math.max(0, Math.min(p, this.totalPages - 1));
  }

  async ngOnInit(): Promise<void> {
    this.pageTitleService.set('決済管理', 's-icon-settlement');
    this.studios = await this.studioService.list('all') ?? [];
    const restored = this.studioSelection.restore(this.studios, SESSION_KEY);
    if (restored) {
      this.selectedStudio = restored;
      await this.loadInvoice();
    }
  }

  async onStudioChange(): Promise<void> {
    if (!this.selectedStudio) this.studioSelection.clear(SESSION_KEY);
    else this.studioSelection.save(this.selectedStudio, SESSION_KEY);
    await this.loadInvoice();
  }

  async loadInvoice(): Promise<void> {
    const requestId = ++this.invoiceRequestId;
    const studio = this.selectedStudio;
    const year = this.selectedYear, month = this.selectedMonth;
    this.items = [];
    this.showFilterOption = false;
    this.pageIndex = 0;
    this.btData = null;
    this.btCsvData = null;
    this.btCsvFileName = '';
    this.selectedFile = null;
    this.showUploadModal = false;
    this.showPayInCashModal = false;
    this.picItem = null;
    if (!studio) { this.searching = false; return; }
    this.searching = true;
    const issueMonth = `${year}-${('0' + month).slice(-2)}`;
    try {
      const [data, btData, exportRows] = await Promise.all([
        this.studioService.getInvoice(studio.id, year, month).catch(() => []),
        this.studioService.getBankTransferUrl(this.getTrusteeCode(studio), year, month).catch(() => null),
        this.studioService.getExportData(studio.id, issueMonth, String(studio.btMethod)).catch(() => []),
      ]);
      if (requestId !== this.invoiceRequestId) return;
      const items = (data ?? []).map((item: any) => ({
        ...item,
        transactionIds: normalizeTransactionIds(item.transactionIds),
      }));
      const exportObj = getExportObj(exportRows ?? [], issueMonth);
      if (items.length > 0) {
        const allIds = items.flatMap((v: any) => v.transactionIds ?? []);
        if (allIds.length > 0) {
          const statusData: Record<number, any> = {};
          const statuses: any[] = await this.transactionService.getStatus({ id: allIds }).catch(() => []) ?? [];
          if (requestId !== this.invoiceRequestId) return;
          statuses.forEach((s: any) => { statusData[s.id] = s; });
          items.forEach((v: any) => {
            const s = statusData[v.transactionIds?.[0]];
            if (s) { v.nowStatus = s.status; v.nowInvoiceId = s.invoiceId; }
          });
        }
      }
      this.items = items;
      this.showFilterOption = items.length > 0;
      this.btData = btData || null;
      this.btCsvData = exportObj.data;
      this.btCsvFileName = exportObj.fileName ?? '';
      this.pageIndex = 0;
    } finally {
      if (requestId === this.invoiceRequestId) this.searching = false;
    }
  }

  private getTrusteeCode(studio: any): string {
    return Number(studio.btMethod) === 1 ? '32368' : (studio.trusteeCode ?? '');
  }

  clearFilter(): void {
    this.keyword = '';
    this.paymentMethodFilter = null;
    this.statusFilter = null;
    this.pageIndex = 0;
  }

  async setMemberDetail(member: any): Promise<void> {
    this.memberDetail = member?.id ? await this.memberService.getOne(member.id) : member;
    this.showMemberModal = true;
  }

  setPayInCash(item: any): void {
    this.picItem = item;
    this.showPayInCashModal = true;
  }

  async payInCash(): Promise<void> {
    if (!this.picItem) return;
    const ids = this.picItem.transactionIds ?? [];
    await this.transactionService.update({ id: ids, status: 9 });
    await this.invoiceService.updateStatus({ id: this.picItem.nowInvoiceId, status: 9 });
    this.showPayInCashModal = false;
    await this.loadInvoice();
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0] ?? null;
  }

  async uploadFile(): Promise<void> {
    if (!this.selectedFile || !this.btData?.id || this.uploading) return;
    this.uploading = true;
    try {
      const formData = new FormData();
      formData.append('file', this.selectedFile);
      await this.studioService.uploadBTImportFile(this.btData.id, formData);
      this.showUploadModal = false;
      this.selectedFile = null;
      const trusteeCode = this.getTrusteeCode(this.selectedStudio);
      this.btData = await this.studioService.getBankTransferUrl(trusteeCode, this.selectedYear, this.selectedMonth).catch(() => null);
    } finally {
      this.uploading = false;
    }
  }

  async downloadBT(): Promise<void> {
    if (this.downloading || !this.btCsvData || !this.btCsvFileName) return;
    this.downloading = true;
    try {
      const data = this.btCsvData.map(row => toShiftJis(`${Object.values(row).join('')}\r\n`));
      const blob = new Blob(data, { type: 'text/plain' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = this.btCsvFileName;
      link.click();
      URL.revokeObjectURL(link.href);
    } finally {
      this.downloading = false;
    }
  }
}
